<template>
  <Teleport to="body">
    <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="emit('close')">
      <div class="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-md mx-4 border border-stroke dark:border-stroke-dark max-h-[80vh] flex flex-col">

        <!-- Header -->
        <div class="flex items-start justify-between px-5 pt-5 pb-4 flex-shrink-0">
          <h3 class="text-base font-semibold text-fg-base dark:text-fg-base-dark">{{ dateLabel }}</h3>
          <button @click="emit('close')" class="ml-3 mt-0.5 p-1 text-fg-faint dark:text-fg-faint-dark rounded transition-colors flex-shrink-0">
            <XMarkIcon class="h-5 w-5" />
          </button>
        </div>

        <!-- Lesson list -->
        <div class="px-5 pb-3 overflow-y-auto flex-1">
          <div v-if="lessons.length === 0" class="text-sm text-fg-muted dark:text-fg-muted-dark py-6 text-center">
            No lessons scheduled
          </div>
          <div v-else class="space-y-1">
            <button
              v-for="l in lessons" :key="l.id"
              @click="selectedLesson = l"
              class="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left transition-colors hover:bg-surface-muted dark:hover:bg-surface-hover-dark"
            >
              <span class="text-xs font-semibold text-fg-base dark:text-fg-base-dark w-14 flex-shrink-0">{{ fmtTime(l.lesson_time) }}</span>
              <span class="w-2 h-2 rounded-full flex-shrink-0" :style="{ background: avatarGradient(l.teacher_name) }" />
              <span class="text-sm text-fg-secondary dark:text-fg-secondary-dark truncate flex-1">
                {{ l.teacher_name || '—' }}<template v-if="l.lesson_title"> · {{ l.lesson_title }}</template>
              </span>
              <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :style="{ background: statusDotColor(lessonStatus(l)) }" />
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-5 py-4 border-t border-stroke dark:border-stroke-dark flex justify-between items-center flex-shrink-0">
          <button @click="emit('close')"
                  class="px-4 py-2 text-sm font-medium rounded-lg border border-stroke dark:border-stroke-dark text-fg-secondary dark:text-fg-secondary-dark transition-colors">
            Close
          </button>
          <button @click="emit('open-day')"
                  class="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white transition-colors">
            {{ openDayLabel }}
          </button>
        </div>
      </div>
    </div>

    <LessonDetailsModal
      v-if="selectedLesson"
      :lesson="selectedLesson"
      :is-editable="isEditable(selectedLesson)"
      @close="selectedLesson = null"
      @edit="editingLesson = selectedLesson; selectedLesson = null"
    />
    <LessonEditModal
      v-if="editingLesson"
      :lesson="editingLesson"
      :allow-locked-single-edit="isAdmin"
      @close="editingLesson = null"
      @saved="applyEdit"
    />
  </Teleport>
</template>

<script setup>
import { ref, computed } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { fmtTime, lessonStatus, statusDotColor, lessonLockTier } from '@/composables/useCalendarData.js'
import { useDemoMode } from '@/composables/useDemoMode'
import { avatarGradient } from '@/composables/useAvatarGradient.js'
import LessonDetailsModal from '@/components/common/LessonDetailsModal.vue'
import LessonEditModal from '@/components/common/LessonEditModal.vue'

const props = defineProps({
  date:    { type: String, required: true },  // iso yyyy-mm-dd
  lessons: { type: Array,  default: () => [] },
  openDayLabel: { type: String, default: 'Open Day View' },
})
const emit = defineEmits(['close', 'open-day', 'lessons-removed'])

const dateLabel = computed(() =>
  new Date(props.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
)

const selectedLesson = ref(null)
const editingLesson  = ref(null)

const effectivePermissions = useDemoMode().effectivePermissionsFor(() => {
  const user = JSON.parse(localStorage.getItem('user') || 'null')
  return user?.permissions
})
const isAdmin = computed(() => effectivePermissions.value.includes('admin'))
// 'paid' locks out everyone (even admins); 'locked' (dispute window closed,
// nothing charged yet) is admin-overridable; 'open' is unrestricted.
const isEditable = lesson => {
  const t = lessonLockTier(lesson)
  if (t === 'paid') return false
  if (t === 'locked') return isAdmin.value
  return true
}

const applyEdit = (updatedLesson) => {
  const target = editingLesson.value
  editingLesson.value = null
  if (!updatedLesson || !target) return
  if (updatedLesson.lesson_time !== undefined) target.lesson_time = updatedLesson.lesson_time
  if (updatedLesson.lesson_date !== undefined) target.lesson_date = updatedLesson.lesson_date
  // See components/common/LessonCard.vue's applyEdit for why these matter — dropping
  // them here left the card showing the pre-edit duration/price after a successful save.
  if (updatedLesson.duration_minutes !== undefined) target.duration_minutes = updatedLesson.duration_minutes
  if (updatedLesson.price_cents !== undefined) target.price_cents = updatedLesson.price_cents
  if (updatedLesson.location !== undefined) target.location = updatedLesson.location
  if (updatedLesson.cancelled_lesson_ids?.length) emit('lessons-removed', updatedLesson.cancelled_lesson_ids)
}
</script>
